import { createFileRoute } from '@tanstack/react-router'
import { Skeleton } from '@/components/ui/skeleton'
import { type EfficacyRow, useEfficacy } from '@/lib/admin-actions'
import { formatExact } from '@/lib/format'
import { cn } from '@/lib/utils'
import { Tag } from '../../-parts'

export const Route = createFileRoute('/_app/admin/actions/_list/efficacy')({
  component: EfficacyPage,
})

/**
 * Whether shipping a kind of check turned out to be worth it.
 *
 * All time and every tenant, because that is the only grain at which the
 * question has an answer. The figure that decides it is the excess: the median
 * of the paired `delta - controlDelta`, which is what one action beat its own
 * site's drift by. A median delta on its own says nothing — a site moves
 * whatever we do — so it never appears here without the control beside it.
 */
function EfficacyPage() {
  const { data, isPending } = useEfficacy()

  if (isPending || !data) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {data.types.map((row) => (
        <Type key={row.type} row={row} />
      ))}
    </div>
  )
}

/** Signed, in the metric's own units. A position moves in places, a click rate
 * moves in points of a percentage, and clicks move in clicks. */
function metricDelta(value: number, metric: string): string {
  const sign = value > 0 ? '+' : ''
  if (metric === 'ctr') return `${sign}${(value * 100).toFixed(2)}pp`
  if (metric === 'position') return `${sign}${value.toFixed(1)}`
  return `${sign}${formatExact(Math.round(value))}`
}

/** Better is up for everything except an average position, which improves by
 * falling — so the sign alone cannot say whether a number is good news. */
function beats(value: number, improves: 'up' | 'down'): boolean {
  return improves === 'up' ? value > 0 : value < 0
}

function Type({ row }: { row: EfficacyRow }) {
  return (
    <section className="overflow-hidden rounded-lg border border-border bg-card">
      <header className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-border bg-secondary/40 px-4 py-2.5">
        <span className="font-mono text-[12.5px]">{row.type}</span>
        <span className="text-[13px] text-muted-foreground">{row.label}</span>
        <span className="ml-auto flex flex-wrap items-center gap-1.5">
          <Tag>{row.nature}</Tag>
          <Tag>{row.metric}</Tag>
          <Tag>{`+${row.measureAfterDays}d`}</Tag>
        </span>
      </header>

      <div className="flex flex-wrap items-start gap-x-10 gap-y-4 px-4 py-4">
        {/* The hero, and the only figure on this page that decides anything. */}
        {row.medianExcess !== null && (
          <div className="min-w-[150px]">
            <p className="mono-label text-muted-foreground">Median excess</p>
            <p
              className={cn(
                'pt-2 font-mono text-[30px] leading-none tabular',
                beats(row.medianExcess, row.improves)
                  ? 'text-success'
                  : row.medianExcess === 0
                    ? undefined
                    : 'text-destructive',
              )}
            >
              {metricDelta(row.medianExcess, row.metric)}
            </p>
          </div>
        )}

        {/* Never one without the other: a move stated without the drift around
            it is taking credit for the weather. */}
        {row.medianDelta !== null && row.medianControlDelta !== null && (
          <>
            <Figure label="median delta" value={metricDelta(row.medianDelta, row.metric)} />
            <Figure
              label="median control"
              value={metricDelta(row.medianControlDelta, row.metric)}
            />
          </>
        )}

        <span className="hidden w-px self-stretch bg-border sm:block" aria-hidden />

        <Figure label="opened" value={formatExact(row.opened)} />
        <Figure label="verified" value={formatExact(row.verified)} />
        <Figure label="completed" value={formatExact(row.completed)} />
        <Figure label="measured" value={formatExact(row.measured)} />
      </div>

      <div className="flex flex-wrap gap-x-8 gap-y-3 border-t border-border px-4 py-3">
        <Figure
          label="improved"
          value={formatExact(row.verdicts.improved)}
          tone={row.verdicts.improved > 0 ? 'good' : undefined}
          small
        />
        <Figure label="unchanged" value={formatExact(row.verdicts.unchanged)} small />
        <Figure
          label="declined"
          value={formatExact(row.verdicts.declined)}
          tone={row.verdicts.declined > 0 ? 'bad' : undefined}
          small
        />
        <Figure label="too early" value={formatExact(row.verdicts.too_early)} small muted />
        <Figure label="unmeasurable" value={formatExact(row.verdicts.unmeasurable)} small muted />
      </div>

      <div className="flex flex-wrap gap-x-8 gap-y-3 border-t border-border px-4 py-3">
        <Figure label="open" value={formatExact(row.states.open)} small />
        <Figure label="in progress" value={formatExact(row.states.in_progress)} small />
        <Figure label="done" value={formatExact(row.states.done)} small />
        <Figure label="resolved" value={formatExact(row.states.resolved)} small />
        <Figure label="dismissed" value={formatExact(row.states.dismissed)} small muted />
        <Figure label="stale" value={formatExact(row.states.stale)} small muted />
      </div>
    </section>
  )
}

function Figure({
  label,
  value,
  tone,
  muted,
  small,
}: {
  label: string
  value: string
  tone?: 'good' | 'bad'
  muted?: boolean
  small?: boolean
}) {
  return (
    <div className="min-w-[64px]">
      <p className="mono-label text-muted-foreground">{label}</p>
      <p
        className={cn(
          'pt-1 font-mono leading-none tabular',
          small ? 'text-[14px]' : 'text-[18px]',
          muted && 'text-muted-foreground',
          tone === 'good' && 'text-success',
          tone === 'bad' && 'text-destructive',
        )}
      >
        {value}
      </p>
    </div>
  )
}
